import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal, Progress, Select, Typography, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { buildAudioUploadFileInputAccept, isAllowedAudioAssetUploadFile } from '../audioUploadAllowlist'
import { AUDIO_UPLOADABLE_PRESENTATION_TYPES, uploadAudioAsset } from '../api'
import type { UploadablePresentationType } from './audioAssetUploadDefaults'

const ACCEPTED_INPUT_ATTR = buildAudioUploadFileInputAccept()

type UploadableType = UploadablePresentationType

/** Generic tenant Audio Upload widget — VISTOERA_TENANT_AUDIO_ASSET_
 * CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 §45/§46/§47. Proxies the raw
 * file straight through this backend to `POST .../audio-assets/upload`
 * (browser -> backend -> R2), same transport shape as
 * `VideoUploadModal`/`saas_video_assets.upload_asset_direct` — never a
 * second presign contract.
 *
 * P0 hard rule (task spec §46/§47): the type picker below NEVER offers
 * "声音模板" (Voice Template) — `AUDIO_UPLOADABLE_PRESENTATION_TYPES`
 * mirrors the backend's own `UPLOADABLE_PRESENTATION_TYPES` allow-list,
 * so a plain uploaded file can never masquerade as a Voice Template
 * (that identity only ever comes from the real Provider enrollment /
 * `CanvasVoiceTemplate` generation flow). */
export default function AudioUploadModal({
  open,
  workspaceId,
  defaultPresentationType,
  onClose,
  onUploaded,
}: {
  open: boolean
  workspaceId: string
  /** Initial category when the modal opens — derived from the active Audio Assets tab. */
  defaultPresentationType: UploadableType
  onClose: () => void
  onUploaded: () => void
}) {
  const { t } = useTranslation(['audioAssets', 'actions'])
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [presentationType, setPresentationType] = useState<UploadableType>(defaultPresentationType)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setPresentationType(defaultPresentationType)
      setFile(null)
      setTitle('')
      setUploading(false)
      setProgress(0)
      setError(null)
    }
    if (!open && wasOpenRef.current) {
      setFile(null)
      setTitle('')
      setUploading(false)
      setProgress(0)
      setError(null)
    }
    wasOpenRef.current = open
  }, [open, defaultPresentationType])

  const pickFile = (f: File | null) => {
    if (!f) return
    if (!isAllowedAudioAssetUploadFile(f)) {
      setError(t('audioAssets:uploadModal.unsupportedFormat'))
      return
    }
    setError(null)
    setFile(f)
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''))
  }

  const onSubmit = async () => {
    if (!file) return
    setUploading(true)
    setError(null)
    setProgress(0)
    try {
      await uploadAudioAsset(workspaceId, file, presentationType, title.trim() || undefined, setProgress)
      message.success(t('audioAssets:toast.uploadSuccess'))
      onUploaded()
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
      const text = typeof msg === 'string' ? msg : msg?.message
      setError(text || (e as Error).message || t('audioAssets:toast.uploadFailed'))
      setUploading(false)
    }
  }

  return (
    <Modal
      open={open}
      title={t('audioAssets:uploadModal.title')}
      onCancel={uploading ? undefined : onClose}
      footer={null}
      destroyOnClose
      centered
    >
      <div style={{ marginBottom: 14 }}>
        <Typography.Text style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('audioAssets:uploadModal.typeLabel')}
        </Typography.Text>
        <Select<UploadableType>
          value={presentationType}
          onChange={setPresentationType}
          disabled={uploading}
          style={{ width: '100%' }}
          data-testid="aac-upload-type-select"
          options={AUDIO_UPLOADABLE_PRESENTATION_TYPES.map((type) => ({
            value: type,
            label: t(`audioAssets:uploadModal.types.${type}`),
          }))}
        />
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          pickFile(e.dataTransfer.files?.[0] || null)
        }}
        style={{
          border: `1px dashed ${dragOver ? 'var(--primary)' : 'var(--border-default)'}`,
          borderRadius: 8,
          padding: '28px 16px',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          background: dragOver ? 'var(--primary-soft)' : 'transparent',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_INPUT_ATTR}
          style={{ display: 'none' }}
          disabled={uploading}
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
        />
        <InboxOutlined style={{ fontSize: 32, color: 'var(--text-muted)' }} />
        <div style={{ marginTop: 8 }}>
          {file ? (
            <Typography.Text strong>{file.name}</Typography.Text>
          ) : (
            <>
              <div>{t('audioAssets:uploadModal.dropHint')}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('audioAssets:uploadModal.formatHint')}
              </Typography.Text>
            </>
          )}
        </div>
      </div>

      {file && (
        <Input
          style={{ marginTop: 16 }}
          placeholder={t('audioAssets:uploadModal.titlePlaceholder')}
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
          disabled={uploading}
        />
      )}

      {uploading && <Progress style={{ marginTop: 16 }} percent={progress} status="active" />}
      {error && (
        <Typography.Text type="danger" style={{ display: 'block', marginTop: 12 }}>
          {error}
        </Typography.Text>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <Button onClick={onClose} disabled={uploading}>
          {t('actions:cancel')}
        </Button>
        <Button type="primary" disabled={!file || uploading} loading={uploading} onClick={() => void onSubmit()}>
          {t('audioAssets:uploadModal.upload')}
        </Button>
      </div>
    </Modal>
  )
}
